import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useGetBoolSettingQuery } from '../../services/settingsApi';
import { useGetRestaurantSettingsQuery } from '../../services/restaurantsApi';
import { useRestaurantTimeZone } from '../../hooks/useTimeZone';
import {
  PRINT_ANIMATION_SETTING_KEY,
  PrintTicketKind,
  PrintTicketPreview,
  subscribePrintAnimation,
} from '../../services/printAnimation';
import './printAnimation.css';

// El papel sale en FEED_MS aunque la impresora conteste antes (la nube encola en ~200 ms):
// más rápido no se alcanza a leer. Si contesta después, el papel espera completo.
const FEED_MS = 1600;
const HOLD_MS = 1300;
const LEAVE_MS = 450;
const FAIL_HOLD_MS = 3500;
// Un servicio que nunca contesta no deja el overlay pegado.
const SAFETY_MS = 20000;

type Phase = 'feeding' | 'printed' | 'sent' | 'failed' | 'leaving';

interface Shown {
  id: number;
  ticket: PrintTicketPreview;
  phase: Phase;
  startedAt: number;
}

const DONE_LABEL: Record<PrintTicketKind, string> = {
  precuenta: 'Precuenta impresa',
  cuenta: 'Ticket de pago impreso',
  corte: 'Corte de caja impreso',
};

const SENT_LABEL: Record<PrintTicketKind, string> = {
  precuenta: 'Precuenta enviada a la impresora',
  cuenta: 'Ticket de pago enviado a la impresora',
  corte: 'Corte de caja enviado a la impresora',
};

const PRINTER_LABEL: Record<PrintTicketKind, string> = {
  precuenta: 'Precuenta',
  cuenta: 'Cuenta',
  corte: 'Corte de caja',
};

/**
 * La impresora que saca el ticket en pantalla. Montado una vez en el layout; sólo escucha
 * cuando el restaurante activó "Animación al imprimir" (Configuración → Operaciones).
 * Nunca bloquea: no recibe clics (pasan a la pantalla de abajo) y cualquier toque o Esc la
 * cierra. Quien imprime sigue dando su propio aviso.
 */
const PrintAnimationOverlay: React.FC = () => {
  const { data: enabledSetting } = useGetBoolSettingQuery(PRINT_ANIMATION_SETTING_KEY);
  const enabled = enabledSetting?.value === true;

  const [shown, setShown] = useState<Shown | null>(null);
  const shownRef = useRef<Shown | null>(null);
  const timers = useRef<number[]>([]);

  const { data: taxBreakdownSetting } = useGetBoolSettingQuery('MostrarDesgloseIVA', { skip: !enabled });
  const showTaxDetail = taxBreakdownSetting?.value ?? true;
  const { data: restaurantSettings } = useGetRestaurantSettingsQuery(undefined, { skip: !shown });
  const { formatInTimeZone } = useRestaurantTimeZone();

  const update = useCallback((next: Shown | null) => {
    shownRef.current = next;
    setShown(next);
  }, []);

  const clearTimers = () => {
    timers.current.forEach(t => window.clearTimeout(t));
    timers.current = [];
  };

  const later = (ms: number, fn: () => void) => {
    timers.current.push(window.setTimeout(fn, ms));
  };

  const dismiss = useCallback(() => {
    const current = shownRef.current;
    if (!current || current.phase === 'leaving') return;
    clearTimers();
    update({ ...current, phase: 'leaving' });
    later(LEAVE_MS, () => update(null));
  }, [update]);

  useEffect(() => {
    if (!enabled) return undefined;
    const unsubscribe = subscribePrintAnimation(event => {
      if (event.type === 'start') {
        clearTimers();
        update({ id: event.id, ticket: event.ticket, phase: 'feeding', startedAt: Date.now() });
        later(SAFETY_MS, dismiss);
        return;
      }
      const current = shownRef.current;
      // El final de un ticket que ya se reemplazó (división en varias cuentas) no aplica, ni
      // el de uno que el usuario ya cerró: no debe volver a aparecer cuando conteste la impresora.
      if (!current || current.id !== event.id || current.phase === 'leaving') return;
      clearTimers();
      if (event.outcome === 'failed') {
        update({ ...current, phase: 'failed' });
        later(FAIL_HOLD_MS, dismiss);
        return;
      }
      const remaining = Math.max(0, FEED_MS - (Date.now() - current.startedAt));
      later(remaining, () => {
        const still = shownRef.current;
        if (!still || still.id !== event.id) return;
        update({ ...still, phase: event.outcome === 'sent' ? 'sent' : 'printed' });
        later(HOLD_MS, dismiss);
      });
    });
    return () => {
      unsubscribe();
      clearTimers();
    };
    // `later`/`clearTimers` sólo tocan refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, dismiss, update]);

  // Escucha en captura y sin detener el evento: el toque que la cierra también llega a lo
  // que estaba debajo (la siguiente mesa, el siguiente cobro), no se pierde.
  const visible = shown !== null;
  useEffect(() => {
    if (!visible) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dismiss();
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('pointerdown', dismiss, true);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerdown', dismiss, true);
    };
  }, [visible, dismiss]);

  if (!enabled || !shown) return null;

  const { ticket, phase } = shown;
  const sections = ticket.sections
    .map(section => section.filter(line => showTaxDetail || !line.taxDetail))
    .filter(section => section.length > 0);

  const statusText = phase === 'failed'
    ? 'No se pudo imprimir'
    : phase === 'feeding'
      ? `Imprimiendo ${PRINTER_LABEL[ticket.kind].toLowerCase()}…`
      : phase === 'sent'
        ? SENT_LABEL[ticket.kind]
        : DONE_LABEL[ticket.kind];

  return (
    <div className={`print-anim print-anim--${phase}`} role="status" aria-live="polite">
      <div className="print-anim__printer" aria-hidden="true">
        <span className="print-anim__printer-name">{PRINTER_LABEL[ticket.kind]}</span>
        <span className="print-anim__led" />
      </div>
      {/* key: cada ticket nuevo (cuentas divididas) vuelve a sacar papel desde arriba */}
      <div key={shown.id} className="print-anim__window" aria-hidden="true">
        <div className="print-anim__paper">
          <div className="print-anim__center print-anim__brand">
            {restaurantSettings?.name || 'MASSRESTAURANT'}
          </div>
          <div className="print-anim__center print-anim__title">{ticket.title}</div>
          <div className="print-anim__center">{formatInTimeZone(new Date(), 'DD/MM/YYYY HH:mm')}</div>
          {sections.map((section, i) => (
            <div key={i} className="print-anim__section">
              {section.map((line, j) => (
                <div key={j} className={`print-anim__row${line.strong ? ' print-anim__row--strong' : ''}`}>
                  <span>{line.left}</span>
                  {line.right && <span>{line.right}</span>}
                </div>
              ))}
            </div>
          ))}
          <div className="print-anim__center print-anim__thanks">
            {ticket.kind === 'corte' ? 'Firma ______________' : '¡Gracias por su visita!'}
          </div>
        </div>
      </div>
      <div className="print-anim__status">
        {(phase === 'printed' || phase === 'sent') && <b className="print-anim__ok">✓</b>}
        {phase === 'failed' && <b className="print-anim__err">✕</b>}
        {statusText}
      </div>
    </div>
  );
};

export default PrintAnimationOverlay;
